// src/components/MobileNavDrawer.js
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import '../assets/styles/MobileNavDrawer.css';

/**
 * MobileNavDrawer
 *
 * Shared hamburger slide-out menu for both header contexts (public NavBar and
 * LoggedInHeader). Pure presenter: each header supplies its own item list
 * wired to its existing navigation handlers, so guards like the Dashboard's
 * unsaved-changes confirmation keep working unchanged.
 *
 * Props:
 *   isOpen   - whether the drawer is visible
 *   onClose  - called on backdrop click, X button, Escape, or after an item click
 *   items    - [{ label, onClick, selected? }]
 *   variant  - 'public' (blue panel) | 'loggedIn' (white panel)
 */
const MobileNavDrawer = ({ isOpen, onClose, items, variant = 'public' }) => {
  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleItemClick = (item) => {
    item.onClick();
    onClose();
  };

  return ReactDOM.createPortal(
    <div className={`ft-mobile-nav ${isOpen ? 'open' : ''} ft-mobile-nav--${variant}`}>
      <div className="ft-mobile-nav-backdrop" onClick={onClose} />
      <div className="ft-mobile-nav-panel" role="dialog" aria-label="Navigation menu">
        <div className="ft-mobile-nav-header">
          <button className="ft-mobile-nav-close" onClick={onClose} aria-label="Close menu">
            <X size={24} />
          </button>
        </div>
        <nav className="ft-mobile-nav-items">
          {items.map((item) => (
            <button
              key={item.label}
              className={`ft-mobile-nav-item ${item.selected ? 'selected' : ''}`}
              onClick={() => handleItemClick(item)}
            >
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </div>,
    document.body
  );
};

export default MobileNavDrawer;
